// Document chips in the transcript
// =====================================================
// A recruiter hands the assistant a job description either as a file or by
// pasting the whole thing into the composer. Both must read as a document chip —
// a wall of pasted JD text in a chat bubble is unreadable and buries the rest of
// the conversation.
//
// This is the single place that decides. It used to be decided inline in
// ChatUser against an allowlist of "document states" (upload_jd, get_jd_upload,
// create_jd, submit_jd, upload_cv, upload_cvs). That allowlist is the wrong
// shape: the assistant files these turns under whichever state its own state
// machine is in, history replays them under yet others, and a message rebuilt
// without a state at all matched nothing — so any step the list didn't name fell
// back to raw text and the chip silently disappeared for that flow.
//
// The rule is inverted here. A long or multi-line block from the recruiter is a
// document *unless* the step is one that genuinely collects prose, and those
// steps are a small closed set: the talent audit's free-text questions (already
// enumerated for flow detection) and the role name. Nothing else in flow_1 or
// flow_2 asks the recruiter to type at length — so a new or renamed document
// step now chips by default instead of silently regressing.

import { isWelcomeOptionLabel } from 'components/chat/recruitment-cards/welcome-options';
import { isTalentAudit, TALENT_AUDIT_STATES } from './assistantFlow';

/**
 * Steps where a long typed answer is the recruiter's own prose, not a document.
 * The talent audit asks open questions about challenges and role context; those
 * answers must stay as text rather than masquerading as a fake attachment.
 */
const PROSE_INPUT_STATES = new Set<string>([
  ...Array.from(TALENT_AUDIT_STATES),
  // The role name. Capped at 100 characters by the composer, so it could never
  // trip the length check anyway — listed so the intent is explicit.
  'get_role',
]);

/** Steps that collect a CV rather than a job description. */
const CV_INPUT_STATES = new Set([
  'upload_cv',
  'upload_cvs',
  'get_cv_upload',
  'upload_resume',
]);

/** Long enough that rendering it as a chat bubble would bury the conversation. */
const LONG_BLOCK_CHARS = 150;

export interface DocumentChip {
  /** Chip headline — the job title where we can find one, else the first line. */
  name: string;
  /** Chip subtitle: "Job Description" or "CV". */
  type: string;
  /**
   * The text the chip stands in for, shown in the preview modal. Empty when the
   * chip is only a filename (a real file upload) and there is nothing to show.
   */
  body: string;
}

export interface DocumentMessageHint {
  /** The API currently omits the original filename from conversation history. */
  name?: string;
  /** Flow 3 accepts several kinds of context files, so "Document" is the default. */
  type?: string;
}

interface DocumentMessageInput {
  content?: string;
  state?: string;
  attachments?: unknown[];
  /** Marks extracted text that answered an upload-enabled assistant prompt. */
  documentHint?: DocumentMessageHint;
}

interface HistoryEntryLike {
  role?: string;
  content?: any;
}

/**
 * The hiring-assistant history endpoint stores an uploaded document as its
 * extracted text and drops the filename/file metadata. The preceding assistant
 * turn still retains `ui_hints.show_upload`, which is the only durable way to
 * distinguish that text from an ordinary Flow 3 answer after a reload.
 */
export const getHistoryDocumentHint = (
  entry: HistoryEntryLike,
  previousEntry?: HistoryEntryLike,
): DocumentMessageHint | undefined => {
  if (entry?.role !== 'user' || typeof entry?.content !== 'string') return undefined;
  if (previousEntry?.role !== 'assistant') return undefined;
  if (previousEntry?.content?.ui_hints?.show_upload !== true) return undefined;

  return { type: 'Document' };
};

/** Marks a text-only submission made through an upload-enabled document input. */
export const getComposerDocumentHint = (
  documentMode: boolean,
  attachments?: unknown[],
): DocumentMessageHint | undefined =>
  documentMode && !attachments?.length ? { type: 'Document' } : undefined;

const documentTypeForState = (state?: string): string =>
  CV_INPUT_STATES.has(state ?? '') ? 'CV' : 'Job Description';

/** Derives a readable chip headline from the top of a pasted document. */
const nameFromBody = (content: string, fallback: string): string => {
  const firstLine = content.split('\n').find((line) => line.trim()) || '';
  const jobTitleMatch = firstLine.match(/job\s*title\s*[:：]\s*(.+)/i);
  if (jobTitleMatch) return jobTitleMatch[1].trim();
  if (firstLine.length > 60) return firstLine.substring(0, 60).trim();
  return firstLine || fallback;
};

/**
 * How a recruiter message should be rendered: a document chip, or null for an
 * ordinary text bubble.
 *
 * `isCandidate` messages are never chips — candidates answer assessment
 * questions at length and none of it is a document.
 */
export const getDocumentChip = (
  message: DocumentMessageInput,
  { isCandidate = false }: { isCandidate?: boolean } = {}
): DocumentChip | null => {
  const content = message.content;
  if (isCandidate || !content) return null;

  // Explicit upload markers the assistant writes into the transcript.
  const matchedPattern = ['JD uploaded:', 'CV uploaded:'].find((pattern) =>
    content.startsWith(pattern)
  );
  if (matchedPattern) {
    const type = matchedPattern.includes('JD') ? 'Job Description' : 'CV';
    const rawPart = content.split('uploaded:')[1]?.trim();

    // A JD upload carries a JSON-ish blob with the file's name and text.
    if (rawPart?.startsWith('{')) {
      try {
        const parsed = JSON.parse(rawPart.replace(/'/g, '"'));
        return {
          name: parsed?.name || 'Uploaded Document',
          type,
          body: parsed?.content || parsed?.text || '',
        };
      } catch (error) {
        console.warn('Failed to parse uploaded file content:', error);
        return { name: 'Uploaded Document', type, body: '' };
      }
    }

    // Simple uploads like "CV uploaded: file_0".
    return { name: rawPart || 'Uploaded File', type, body: '' };
  }

  // A welcome card writes its full description into the transcript as the
  // recruiter's line. It's one long sentence, not a document — and matching it
  // exactly beats hoping it stays under the length threshold.
  if (isWelcomeOptionLabel(content)) return null;

  const isLongBlock = content.includes('\n') || content.length > LONG_BLOCK_CHARS;

  // On history replay an uploaded Flow 3 document has become plain extracted
  // text. The upload-prompt hint restores the card and gives the preview modal
  // the full body. A short typed reply to the same prompt remains a normal bubble.
  if (message.documentHint && isLongBlock) {
    const type = message.documentHint.type || 'Document';
    return {
      name: message.documentHint.name || nameFromBody(content, 'Uploaded Document'),
      type,
      body: content,
    };
  }

  // The remaining Flow 3 branches are server-driven and their state names are
  // deliberately not hardcoded in the client. While an audit is active, a long
  // typed response is therefore prose unless it carries one of the explicit
  // upload markers handled above. Live file uploads render through the uploaded
  // document component; replayed uploads are restored by `documentHint` above.
  const isProseStep = PROSE_INPUT_STATES.has(message.state ?? '') || isTalentAudit();
  const isDocument = !!message.attachments?.length || (!isProseStep && isLongBlock);
  if (!isDocument || !isLongBlock) return null;

  const type = documentTypeForState(message.state);
  return {
    name: nameFromBody(content, type),
    type,
    body: content,
  };
};
